import { Text, Tooltip } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { FC, useCallback, useMemo } from 'react';
import { ActionMeta, GroupBase, MultiValue } from 'react-select';
import { SelectComponents } from 'react-select/dist/declarations/src/components';

export type Option = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

export type MultiSelectInputProps = {
  value: Option[];
  options: Option[];
  onChange: (value: Option[], actionMeta: ActionMeta<Option>) => void;
  disabled?: boolean;
  name: string;
  id?: string;
  placeholder: string;
  isRequired?: boolean;
};

const SELECT_ALL = '<SELECT_ALL>';
const MAX_TO_SHOW = 1;
const getLabel = (inputType: string, id?: string) => {
  if (id === 'AccessSelect-Module') return 'All';

  const labels: Record<string, string> = {
    Districts: 'All',
    AccessSelect: 'Global',
  };

  return labels[inputType] || 'All';
};

const TooltipRenderer: SelectComponents<Option, true, GroupBase<Option>>['MultiValueContainer'] = ({
  data,
  sx,
  selectProps: { value, options, name },
}) => {
  if (!value || !('length' in value)) return null;
  const allSelectedLabel = value.map((option) => option.label).join(', ');
  if (value.length >= options.length - 1) {
    if (data.value === value[0]?.value) {
      return (
        <Tooltip label={allSelectedLabel}>
          <Text sx={sx}>{getLabel(name || '')}</Text>
        </Tooltip>
      );
    } else {
      return null;
    }
  } else if (value.slice(0, MAX_TO_SHOW).some((v) => v.value === data.value)) {
    return (
      <Tooltip label={allSelectedLabel}>
        <Text sx={sx}>{data.label}</Text>
      </Tooltip>
    );
  } else if (data.value === value[MAX_TO_SHOW]?.value) {
    return (
      <Tooltip label={allSelectedLabel}>
        <Text sx={sx}>{`+${value.length - MAX_TO_SHOW}`}</Text>
      </Tooltip>
    );
  } else {
    return null;
  }
};

const MultiSelectInput: FC<MultiSelectInputProps> = ({
  name,
  id,
  value,
  options,
  onChange,
  disabled = false,
  placeholder,
  isRequired = false,
}) => {
  const isSelectAllSelected = value.length >= options.length;
  const isOptionSelected = useCallback(
    (option: Option): boolean =>
      isSelectAllSelected || value.some(({ value }) => value === option.value),
    [value, isSelectAllSelected]
  );
  // Extracted independent statements above the Select component
  let selectWidth: string | undefined;
  if (name === 'deepDive') {
    selectWidth = '100px';
  } else if (name === 'shareUser') {
    selectWidth = '180px';
  } else {
    selectWidth = undefined;
  }
  let selectMinHeight: string | undefined;
  if (name === 'ResultPieChart') {
    selectMinHeight = '33px';
  } else if (name === 'globalInput') {
    selectMinHeight = '36px';
  } else {
    selectMinHeight = undefined;
  }
  let multiValueFontSize: string;
  if (name === 'shareUser') {
    multiValueFontSize = '8px !important';
  } else if (name === 'globalInput') {
    multiValueFontSize = '8px';
  } else {
    multiValueFontSize = '10px !important';
  }
  const fullOptions = useMemo(() => {
    if (!options || options.length === 0) {
      return [];
    }

    return [
      {
        value: SELECT_ALL,
        label: getLabel(name),
      },
      ...options,
    ];
  }, [options, name]);
  const handleOnChange = useCallback(
    (newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>): void => {
      const { action, option, removedValue } = actionMeta;
      if (action === 'select-option' && option?.value === SELECT_ALL) {
        onChange(
          options.filter((o) => !o.isDisabled),
          actionMeta
        );
      } else if (
        (action === 'deselect-option' && option?.value === SELECT_ALL) ||
        (action === 'remove-value' && removedValue?.value === SELECT_ALL)
      ) {
        onChange([], actionMeta);
      } else if (actionMeta.action === 'deselect-option' && isSelectAllSelected) {
        const updatedOptions = options.filter(({ value }) => value !== option?.value);
        onChange(updatedOptions, actionMeta);
      } else {
        onChange([...newValue], actionMeta);
      }
    },
    [isSelectAllSelected, onChange, options]
  );

  return (
    <Select
      name={name}
      id={id}
      menuPortalTarget={document.body}
      required={isRequired}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 999999 }),
        menu: (base) => ({ ...base, zIndex: 999999 }),
      }}
      chakraStyles={{
        menuList: (baseStyles) => ({
          ...baseStyles,
          maxHeight: '168px',
          overflowX: 'hidden',
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          minWidth: 'fit-content',
        }),
        // Let the "globalInput" select shrink/grow to its content width
        // (bounded below by minWidth on control) instead of being stretched
        // to 100% of the grid cell, so it can widen past its default size to
        // fit selections. Other callers (e.g. regional filters) keep the
        // existing 100%-of-container behavior.
        container: (baseStyles) => ({
          ...baseStyles,
          width: name === 'globalInput' ? 'fit-content' : baseStyles.width,
        }),
        control: (baseStyles, state) => {
          const isAccessSelect = name === 'AccessSelect' || name === 'AccessSelect1';
          const textAlignment = isAccessSelect ? 'left' : 'center';

          return {
            ...baseStyles,
            fontWeight: '600',
            fontSize: '10px',
            textAlign: textAlignment,
            color: '#1E1E1ECC',
            bg: 'white',
            minWidth: name === 'globalInput' ? '160px' : '120px',
            width: selectWidth ?? (name === 'globalInput' ? 'fit-content' : undefined),
            minHeight: selectMinHeight,
            padding: '0 16px 0',
            backgroundColor: 'white !important',
            borderColor: state.isFocused ? 'primaryBlack !important' : 'border !important',
            borderRadius: '8px',
            boxShadow: 'none !important',
          };
        },
        placeholder: (baseStyles) => ({
          ...baseStyles,
          color: '#1E1E1ECC',
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          px: '0',
          fontSize: '10px',
          fontWeight: '600',
          // Content is bounded to at most 2 chips (selected label + "+N" badge,
          // see TooltipRenderer/MAX_TO_SHOW). Keep them on a single line and
          // let the control grow wider (via container/control width above)
          // instead of wrapping to a second line.
          flexWrap: 'nowrap',
          overflowY: 'visible',
        }),

        multiValue: (baseStyles) => ({
          ...baseStyles,
          padding: name === 'globalInput' ? '1' : 'auto',
          fontWeight: '600',
          color: '#1E1E1ECC',
          bg: 'linear-gradient(rgba(245, 224, 3, 0.5), rgba(229, 182, 17, 0.5))',
          fontSize: multiValueFontSize,
        }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          px: '2px',
          bg: 'none',
          cursor: 'pointer',
        }),
        clearIndicator: (baseStyles) => ({
          ...baseStyles,
          bg: 'none',
          fontSize: '10px',
          w: 'auto',
          px: '2px',
          mx: '0',
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          fontSize: '10px',
          fontWeight: '600',
        }),
        option: (baseStyles, state) => ({
          ...baseStyles,
          color: '#1E1E1ECC !important',
          zIndex: 10,
          textTransform: 'uppercase',
          fontSize: name === 'ResultPieChart' ? '8px' : '10px',
          fontWeight: state.isSelected ? '700' : '600',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          background: state.isSelected
            ? 'linear-gradient(rgba(245, 224, 3, 0.5), rgba(229, 182, 17, 0.5)) !important'
            : 'none',
          _hover: {
            color: '#1E1E1ECC',
            bg: 'linear-gradient(rgba(245, 224, 3, 0.5), rgba(229, 182, 17, 0.5))',
          },
        }),
      }}
      selectedOptionStyle="check"
      options={fullOptions}
      value={[
        ...new Map(
          value.filter((item) => item.value !== 'ALL').map((item) => [item.value, item])
        ).values(),
      ]}
      isOptionSelected={isOptionSelected}
      onChange={handleOnChange}
      classNamePrefix="topSelectFilter amt"
      className="topSelectFilter basic-multi-select"
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isMulti
      placeholder={placeholder}
      isDisabled={disabled ?? false}
      components={{
        IndicatorSeparator: null,
        MultiValueContainer: TooltipRenderer,
      }}
    />
  );
};

export default MultiSelectInput;
